import { Fragment, useMemo, useState } from 'react';
import BaseDialog from '@/components/ui/BaseDialog';
import { ProductDetailsProduct } from '@/types/pages/storeManagement/product';
import { getApiBaseUrl } from '@/utils/env';
import {
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { useAppContext } from '@/provider/contexts';
import { fetchProductDetails } from '@/api/storeManagement/product';
import noPreviewImage from '@/assets/images/no-preview.png';
import styleVariables from '@/styles/style.module.scss';
import { useTranslation } from 'react-i18next';

interface ViewProductDialogProps {
    isDialogOpen: boolean;
    barcode: string;
    formatUtcToStoreTime: (utcTime: string) => string;
    onClose: () => void;
}

type ProductDisplayKey =
    | 'number'
    | 'barcode'
    | 'name'
    | 'area'
    | 'unit'
    | 'price'
    | 'promo_price'
    | 'default_layout'
    | 'custom_fields'
    | 'created_by'
    | 'created_at'
    | 'updated_at'
    | 'updated_by';
interface TableConfig {
    title: string;
    key: ProductDisplayKey;
}

const API_BASE_URL = getApiBaseUrl();

const getImageUrl = (imgPath: string | null | undefined) => {
    if (!imgPath || imgPath.trim() === '') {
        return noPreviewImage;
    }

    return `${API_BASE_URL}${imgPath}`;
};

const Loading = () => {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160 }}>
            <CircularProgress />
        </div>
    );
};

const StatusChip = ({ status }: { status?: string }) => {
    const { t } = useTranslation('common');
    const isOnline = status?.toLowerCase() === 'online';
    return (
        <>
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    color: isOnline ? styleVariables.successColor : styleVariables.errorColor,
                }}
            >
                <span
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: isOnline ? styleVariables.successColor : styleVariables.errorColor,
                        display: 'inline-block',
                    }}
                />
                {isOnline ? t('ONLINE') : t('OFFLINE')}
            </span>
        </>
    );
};

const PRODUCT_CONFIG: TableConfig[] = [
    { title: 'Number', key: 'number' },
    { title: 'Barcode', key: 'barcode' },
    { title: 'Name', key: 'name' },
    { title: 'Area', key: 'area' },
    { title: 'Unit', key: 'unit' },
    { title: 'Price', key: 'price' },
    { title: 'Promo Price', key: 'promo_price' },
    { title: 'Default Layout', key: 'default_layout' },
    { title: 'Custom Fields', key: 'custom_fields' },
];

const SYSTEM_CONFIG: TableConfig[] = [
    { title: 'Created by', key: 'created_by' },
    { title: 'Created at', key: 'created_at' },
    { title: 'Updated at', key: 'updated_at' },
    { title: 'Updated by', key: 'updated_by' },
];
const ViewProductDialog = ({ isDialogOpen, barcode, formatUtcToStoreTime, onClose }: ViewProductDialogProps) => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [productData, setProductData] = useState<ProductDetailsProduct | null>(null);

    const deviceList = useMemo(() => {
        if (!productData) return [];
        return productData.devices.map((o) => ({
            ...o,
            img: getImageUrl(o.img),
        }));
    }, [productData]);

    const handleOnEnter = async () => {
        setLoading(true);
        try {
            const {
                data: { data, isSuccess },
            } = await fetchProductDetails(manageStoreId as number, barcode);
            if (isSuccess) {
                setProductData(data.products?.[0] || null);
                return;
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                setNotification({
                    message: error?.message || t('Failed to Fetch Product Detail Info'),
                    severity: 'error',
                });
                onClose();
                return;
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOnExited = () => {
        setProductData(null);
    };

    const formatData = (key: ProductDisplayKey) => {
        if (!productData) return '-';
        const value = (productData as ProductDetailsProduct)[key];
        if (key === 'created_at' || key === 'updated_at') {
            return formatUtcToStoreTime(value as string);
        }

        if (key === 'default_layout') {
            return value === null || value === undefined || value === '' ? t('Not set') : String(value);
        }
        return value ?? '-';
    };
    return (
        <BaseDialog
            open={isDialogOpen}
            maxWidth="md"
            dialogTitle={t('View Product Detail')}
            onClose={onClose}
            disableConfirm
            TransitionProps={{
                onEnter: handleOnEnter,
                onExited: handleOnExited,
            }}
        >
            {loading ? (
                <Loading />
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            columnGap: '1.5rem',
                            rowGap: '0.8rem',
                            marginTop: '0.4rem',
                        }}
                    >
                        {/* 商品資訊 */}
                        <section>
                            <h3 style={{ margin: '0 0 0.5rem' }}>{t('Product')}</h3>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '120px 1fr', // 左列固定寬度，右列彈性
                                    rowGap: '6px',
                                    columnGap: '12px',
                                    fontSize: '0.92rem',
                                }}
                            >
                                {PRODUCT_CONFIG.map((config) => (
                                    <Fragment key={config.key}>
                                        <span style={{ color: '#666' }}>{t(config.title)}</span>
                                        <span
                                            style={{
                                                wordBreak: 'break-word',
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {formatData(config.key)}
                                        </span>
                                    </Fragment>
                                ))}
                            </div>
                        </section>

                        {/* 系統資訊 */}
                        <section>
                            <h3 style={{ margin: '0 0 0.5rem' }}>{t('System')}</h3>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '120px 1fr',
                                    rowGap: '6px',
                                    columnGap: '12px',
                                    fontSize: '0.92rem',
                                }}
                            >
                                {SYSTEM_CONFIG.map((config) => (
                                    <Fragment key={config.key}>
                                        <span style={{ color: '#666' }}>{t(config.title)}</span>
                                        <span>{formatData(config.key)}</span>
                                    </Fragment>
                                ))}
                            </div>
                        </section>
                    </div>
                    <div>
                        <h3 style={{ margin: '0.5rem 0' }}>{t('Devices')}</h3>
                        {deviceList.length === 0 ? (
                            <p>-</p>
                        ) : (
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                                <Table stickyHeader size="small" aria-label={t('ESL table')}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('Device')}</TableCell>
                                            <TableCell>{t('Layout')}</TableCell>
                                            <TableCell>{t('Size')}</TableCell>
                                            <TableCell>{t('Update Time')}</TableCell>
                                            <TableCell>{t('Preview')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {deviceList.map((e) => {
                                            const layoutText = e.layout?.single
                                                ? e.layout.single
                                                : Array.isArray(e.layout?.multiple)
                                                  ? e.layout.multiple.join(' ')
                                                  : '-';

                                            return (
                                                <TableRow key={e.mac}>
                                                    <TableCell>
                                                        <div
                                                            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                                                        >
                                                            <Tooltip title={t('click to copy')} arrow>
                                                                <span
                                                                    style={{ cursor: 'copy', fontWeight: 600 }}
                                                                    onClick={() =>
                                                                        navigator.clipboard?.writeText?.(e.mac)
                                                                    }
                                                                >
                                                                    {e.mac || '-'}
                                                                </span>
                                                            </Tooltip>
                                                            <StatusChip status={e.ed_status} />
                                                        </div>
                                                    </TableCell>

                                                    <TableCell>
                                                        <span
                                                            style={{
                                                                display: 'inline-block',
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-word',
                                                                overflowWrap: 'anywhere',
                                                                maxWidth: 320,
                                                            }}
                                                        >
                                                            {layoutText}
                                                        </span>
                                                    </TableCell>

                                                    <TableCell>{e.image_resolution ?? '-'}</TableCell>
                                                    <TableCell>
                                                        {formatUtcToStoreTime(e.last_job_status_time)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {e.img ? (
                                                            <img
                                                                src={e.img}
                                                                alt={t('preview')}
                                                                style={{
                                                                    width: 200,
                                                                    height: 100,
                                                                    objectFit: 'contain',
                                                                    border: 'none',
                                                                    borderRadius: 4,
                                                                }}
                                                            />
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </div>
                </div>
            )}
        </BaseDialog>
    );
};

export default ViewProductDialog;
